import { useEffect, useMemo, useRef, useState } from 'react';
import SubstanceTile from './SubstanceTile';
import ElementSearch from './ElementSearch';
import { PERIODIC_TABLE_CLASSES, TILE_CLASSES } from '../constants/classNames';
import { CATEGORY_LABELS, SUBSTANCE_CATEGORIES } from '../constants/effects';
import { F_BLOCK_MARKERS } from '../utils/periodicTableUtils';
import { joinClassNames } from '../utils/classNameUtils';
import { searchSubstances } from '../utils/searchUtils';
import { scrollTileIntoView } from '../utils/domUtils';
import '../styles/PeriodicTable.css';

function CategoryLegend({ categories, highlightedCategory, onToggleCategory, onShowAll }) {
  const isShowingAll = highlightedCategory === null;
  return (
    <div className={PERIODIC_TABLE_CLASSES.legend} role="group" aria-label="Highlight an element family">
      <button
        type="button"
        aria-pressed={isShowingAll}
        className={joinClassNames(PERIODIC_TABLE_CLASSES.legendButton, isShowingAll && PERIODIC_TABLE_CLASSES.legendButtonActive)}
        onClick={onShowAll}
      >
        All
      </button>
      {categories.map((category) => (
        <button
          key={category}
          type="button"
          aria-pressed={highlightedCategory === category}
          className={joinClassNames(
            PERIODIC_TABLE_CLASSES.legendButton,
            highlightedCategory === category && PERIODIC_TABLE_CLASSES.legendButtonActive
          )}
          onClick={() => onToggleCategory(category)}
        >
          <span
            className={joinClassNames(PERIODIC_TABLE_CLASSES.legendSwatch, `${TILE_CLASSES.categoryPrefix}${category}`)}
            aria-hidden="true"
          />
          {CATEGORY_LABELS[category]}
        </button>
      ))}
    </div>
  );
}

/**
 * Not an element: shows where the lanthanides/actinides belong and points to their row below.
 */
function FamilyMarker({ marker, isDimmed }) {
  const { category, familyName, range, gapPosition, labelPosition } = marker;
  const markerClassName = `${PERIODIC_TABLE_CLASSES.familyMarkerPrefix}${category}`;
  return (
    <>
      <div
        style={{ gridRow: gapPosition.row, gridColumn: gapPosition.column }}
        className={joinClassNames(PERIODIC_TABLE_CLASSES.familyMarker, markerClassName, isDimmed && TILE_CLASSES.dimmed)}
        title={`The ${familyName} (${range}) sit in this spot. They are shown in their own row below.`}
      >
        <span aria-hidden="true">↓</span>
        {range}
      </div>
      <div
        style={{ gridRow: labelPosition.row, gridColumn: labelPosition.column }}
        className={joinClassNames(PERIODIC_TABLE_CLASSES.familyRowLabel, markerClassName, isDimmed && TILE_CLASSES.dimmed)}
      >
        {familyName} →
      </div>
    </>
  );
}

/**
 * The full 118-element periodic table plus a row of ready-made compounds (lab bottles).
 */
export default function PeriodicTable({ substances, selectedIds, onSelectSubstance }) {
  const [highlightedCategory, setHighlightedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const scrollRef = useRef(null);

  const elements = useMemo(() => substances.filter((substance) => substance.isElement()), [substances]);
  const compounds = useMemo(() => substances.filter((substance) => !substance.isElement()), [substances]);
  const elementCategories = useMemo(() => [...new Set(elements.map((element) => element.category))], [elements]);

  const searchMatches = useMemo(() => searchSubstances(substances, searchQuery), [substances, searchQuery]);
  const matchIds = useMemo(() => new Set(searchMatches.map((substance) => substance.id)), [searchMatches]);
  const isSearching = searchQuery.trim().length > 0;
  const bestMatchId = searchMatches[0]?.id;

  // Bring the best match into view as the kid types.
  useEffect(() => {
    if (!bestMatchId || !scrollRef.current) return;
    const section = scrollRef.current.closest('section');
    const searchBar = section.querySelector('[role="search"]');
    const coveredTopPx = searchBar ? searchBar.getBoundingClientRect().bottom : 0;
    scrollTileIntoView(section.querySelector(`[data-substance-id="${bestMatchId}"]`), scrollRef.current, coveredTopPx);
  }, [bestMatchId]);

  const clearSearch = () => setSearchQuery('');

  const pickBestMatch = () => {
    if (!bestMatchId) return;
    if (!selectedIds.includes(bestMatchId)) onSelectSubstance(bestMatchId);
    clearSearch();
  };

  const toggleHighlightedCategory = (category) => {
    clearSearch();
    setHighlightedCategory((current) => (current === category ? null : category));
  };

  const showAllCategories = () => {
    clearSearch();
    setHighlightedCategory(null);
  };

  // While searching, everything except the matches fades; otherwise the family legend decides.
  const isDimmed = (category, substanceId) => {
    if (isSearching) return !matchIds.has(substanceId);
    return highlightedCategory !== null && highlightedCategory !== category;
  };

  return (
    <section className={PERIODIC_TABLE_CLASSES.root} aria-labelledby="periodic-table-title">
      <div className={PERIODIC_TABLE_CLASSES.header}>
        <h2 id="periodic-table-title" className={PERIODIC_TABLE_CLASSES.title}>
          Pick two to mix
        </h2>
      </div>

      <ElementSearch
        query={searchQuery}
        matchCount={searchMatches.length}
        onChangeQuery={setSearchQuery}
        onClearQuery={clearSearch}
        onPickBestMatch={pickBestMatch}
      />

      <div className={PERIODIC_TABLE_CLASSES.bottles}>
        <span className={PERIODIC_TABLE_CLASSES.bottlesLabel}>Lab bottles:</span>
        {compounds.map((compound) => (
          <SubstanceTile
            key={compound.id}
            substance={compound}
            isSelected={selectedIds.includes(compound.id)}
            isDimmed={isDimmed(SUBSTANCE_CATEGORIES.COMPOUND, compound.id)}
            isSearchMatch={matchIds.has(compound.id)}
            onSelect={onSelectSubstance}
          />
        ))}
      </div>

      <CategoryLegend
        categories={elementCategories}
        highlightedCategory={isSearching ? undefined : highlightedCategory}
        onToggleCategory={toggleHighlightedCategory}
        onShowAll={showAllCategories}
      />

      <p className={PERIODIC_TABLE_CLASSES.swipeHint}>Swipe sideways to see the whole table →</p>

      <div className={PERIODIC_TABLE_CLASSES.scroll} ref={scrollRef}>
        <div className={PERIODIC_TABLE_CLASSES.grid}>
          {F_BLOCK_MARKERS.map((marker) => (
            <FamilyMarker key={marker.category} marker={marker} isDimmed={isSearching || isDimmed(marker.category)} />
          ))}
          {elements.map((element) => (
            <SubstanceTile
              key={element.id}
              substance={element}
              gridPosition={element.tablePosition}
              isSelected={selectedIds.includes(element.id)}
              isDimmed={isDimmed(element.category, element.id)}
              isSearchMatch={matchIds.has(element.id)}
              onSelect={onSelectSubstance}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
